import React from "react";
import { ClientApartment } from "../../../utils/api/apartmentRentalsAPI";
import { CalDate, isoOf, pad2 } from "./bookingPricing";
import Calendar, { DayMap, CalendarLabels } from "./Calendar";
import PromoBanners from "./PromoBanners";

interface Props {
  apt: ClientApartment;
  calYear: number;
  calMonth: number;
  availability: DayMap;
  calLoading: boolean;
  checkIn: CalDate | null;
  checkOut: CalDate | null;
  nights: number;
  minNights: number;
  onSelectDay: (y: number, m: number, d: number) => void;
  onPrev: () => void;
  onNext: () => void;
  canGoPrev: boolean;
  canGoNext: boolean;
  windowCutoff: Date | null;
  windowLabel: string | null;
  rangeError: string | null;
  onResetDates: () => void;
  monthNames: string[];
  dowLabels: string[];
  calLabels: CalendarLabels;
  daysUntilCheckIn: number | null;
  lang: string;
  t: (key: any) => any;
  selectingPhase: "checkin" | "checkout";
  onTypeCheckIn: (iso: string) => void;
  onTypeCheckOut: (iso: string) => void;
}

const StepDates: React.FC<Props> = ({
  apt, calYear, calMonth, availability, calLoading, checkIn, checkOut, nights, minNights,
  onSelectDay, onPrev, onNext, canGoPrev, canGoNext, windowCutoff, windowLabel, rangeError,
  onResetDates, monthNames, dowLabels, calLabels, daysUntilCheckIn, lang, t, selectingPhase,
  onTypeCheckIn, onTypeCheckOut,
}) => {
  const todayIso = new Date().toISOString().split("T")[0];

  const minCheckoutIso = checkIn
    ? (() => {
        const d = new Date(checkIn.year, checkIn.month - 1, checkIn.day + minNights);
        return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
      })()
    : undefined;

  return (
    <div className="pab-step">
      <div className="pab-step__title-row">
        <h2 className="pab-step__title">{lang === "en" ? "When are you staying?" : "Kada dolazite?"}</h2>
        {(checkIn || checkOut) && (
          <button type="button" className="pab-step__reset" onClick={onResetDates}>
            × {lang === "en" ? "Reset" : "Resetuj"}
          </button>
        )}
      </div>

      {/* Date pills — editable native date inputs, so typing works as well as tapping the calendar */}
      <div className="pab-date-pills">
        <label className={`pab-date-pill${checkIn ? " pab-date-pill--filled" : selectingPhase === "checkin" ? " pab-date-pill--active" : ""}`}>
          <span className="pab-date-pill__label">{t("checkIn")}</span>
          <input
            type="date"
            className="pab-date-pill__input"
            value={isoOf(checkIn)}
            min={todayIso}
            onChange={(e) => onTypeCheckIn(e.target.value)}
          />
        </label>
        <div className="pab-date-pill__arrow">→</div>
        <label className={`pab-date-pill${checkOut ? " pab-date-pill--filled" : selectingPhase === "checkout" ? " pab-date-pill--active" : ""}${!checkIn ? " pab-date-pill--disabled" : ""}`}>
          <span className="pab-date-pill__label">{t("checkOut")}</span>
          <input
            type="date"
            className="pab-date-pill__input"
            value={isoOf(checkOut)}
            min={minCheckoutIso || todayIso}
            disabled={!checkIn}
            onChange={(e) => onTypeCheckOut(e.target.value)}
          />
        </label>
      </div>

      {nights > 0 && (
        <div className="pab-nights-pill">
          🌙 {nights} {nights === 1 ? (lang === "en" ? "night" : "noć") : (lang === "en" ? "nights" : "noći")}
        </div>
      )}

      {!checkIn && (
        <div className="pab-hint-text">
          {lang === "en"
            ? "Tap a date in the calendar for each night you'll stay, or type the dates above"
            : "Tapnite u kalendaru svaku noć koju ostajete, ili unesite datume iznad"}
          {minNights > 1 && ` (min. ${minNights} ${lang === "en" ? "nights" : "noći"})`}
        </div>
      )}

      {(windowLabel || minNights > 1) && (
        <div className="pab-notice-row">
          {windowLabel && <div className="pab-notice pab-notice--info">📅 {windowLabel}</div>}
          {minNights > 1 && (
            <div className="pab-notice pab-notice--info">
              🌙 {lang === "en" ? "Minimum stay" : "Minimalan boravak"}: <strong>{minNights} {lang === "en" ? "nights" : "noći"}</strong>
            </div>
          )}
        </div>
      )}

      {rangeError && (
        <div className="pab-notice pab-notice--error">
          <span>⛔</span>
          <span>{rangeError}</span>
        </div>
      )}

      {calLoading ? (
        <div className="pab-cal-skeleton">
          <div className="pab-skeleton pab-skeleton--cal-nav" />
          <div className="pab-skeleton-grid">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="pab-skeleton pab-skeleton--cal-day" />
            ))}
          </div>
        </div>
      ) : (
        <Calendar
          year={calYear}
          month={calMonth}
          dayMap={availability}
          apt={apt}
          checkIn={checkIn}
          checkOut={checkOut}
          onSelectDay={onSelectDay}
          onPrev={onPrev}
          onNext={onNext}
          canGoPrev={canGoPrev}
          canGoNext={canGoNext}
          cutoff={windowCutoff}
          monthNames={monthNames}
          dowLabels={dowLabels}
          labels={calLabels}
        />
      )}

      {apt && (apt.lastMinute?.enabled || apt.earlyBooking?.enabled) && (
        <PromoBanners apt={apt} daysUntilCheckIn={daysUntilCheckIn} />
      )}
    </div>
  );
};

export default StepDates;
